/**
 * 尺码表弹窗「默认 tab / Bra 表类型」的补丁。
 *
 * 背景：切 tab 的逻辑本身不在主题里，而在尺码表 Shopify Page 的 page.content 自带的
 * <script> 里（那份内容与老主题共用、不可改动）。它读 .closest('.product-popup-modal')
 * 上的 data-default-category 来决定默认打开哪个 tab，以及 Bra 用大表还是小表
 * （见 snippets/fanka-size-guide.liquid）。
 *
 * 但那段 JS 只在 DOMContentLoaded 跑一次，而 Horizon 每次变体切换都会 morph、
 * 用服务端 HTML 把弹窗内容整段冲掉。更糟的是 Page 的原始 HTML 自身是矛盾的：
 *   <div class="tab-item active" data-tab="Bottoms">      ← tab 高亮在 Bottoms
 *   <div class="size-table active" data-tab="Bra" ...>    ← 表格却显示 Bra
 * 靠它自己的 updateSizeTableDisplay() 在启动时对齐。morph 之后没人再对齐，
 * 于是变成「tab 高亮 Bottoms、表格内容 Bra」的错乱态，且默认 tab 也丢了。
 * （实测复现：刚加载时正确 → 切一次变体 → 打开弹窗即错乱。）
 *
 * 所以这里在**每次打开弹窗时**重新应用一次默认 tab，并按品类激活正确的 Bra 表。
 *
 * 两个刻意的设计选择：
 * - 用 tab.click() 而不是自己改 active class：复用 Page 自带的切换逻辑（它同时管
 *   active class 与表格显隐、还有 in/cm 单位维度），避免重复实现、也不依赖其内部结构。
 * - 用一个「已应用」标记记在 .tab-nav 上（而不是记在 JS 变量里）：morph 会把这个
 *   标记一起冲掉，正好等价于「内容被重渲染了，需要重新应用」；而用户在弹窗里手动切了
 *   tab 再关掉重开时标记还在，就不去覆盖他的选择 —— 与老主题「每次页面加载定位一次」的
 *   语义一致。
 */
const DIALOG_SELECTOR = '.fanka-size-guide-link__dialog';
const APPLIED_FLAG = 'data-fanka-default-tab-applied';

/**
 * 把 data-default-category（小写：tops / bottoms / bra / bra-small）映射成 tab 的 data-tab。
 * Bra 与 Bra-Small 共用同一个 Bra tab，表类型靠 data-type 区分。
 */
function resolveTabName(category) {
  const value = category.toLowerCase();
  if (value.includes('bra')) return 'Bra';
  if (value === 'tops') return 'Tops';
  if (value === 'bottoms') return 'Bottoms';
  return '';
}

/**
 * bar-size-detach-chart 的 Bra tab 有两套表：
 *   data-type="bra"       ← default_category 恰好为 bra
 *   data-type="bra-small" ← bra-small（以及其它非精确 bra 的回退）
 * Page 自带 JS 把选择记在闭包变量 braTableType 里；morph 后闭包失效，
 * 这里按同一规则直接激活对应表，避免 Bra 商品被锁在小表上。
 */
function activateBraTable(dialog, category) {
  if (!category.includes('bra')) return;

  const braTableType = category === 'bra' ? 'bra' : 'bra-small';
  const activeUnit =
    dialog.querySelector('.unit-option.active')?.getAttribute('data-unit') || 'in';

  dialog.querySelectorAll('.size-table').forEach((table) => {
    table.classList.remove('active');
    const tableTab = table.getAttribute('data-tab');
    const tableUnit = table.getAttribute('data-unit');
    const tableType = table.getAttribute('data-type');
    const match =
      tableTab === 'Bra' &&
      tableUnit === activeUnit &&
      (!tableType || tableType === braTableType);
    if (match) table.classList.add('active');
  });
}

/**
 * 把弹窗的 tab 切到该商品品类对应的那个：
 * - dialog：尺码表弹窗元素（带 data-default-category）
 * 属性缺失（元字段为空）时什么都不做，保持 Page 自己的默认。
 */
function applyDefaultTab(dialog) {
  const category = (dialog.getAttribute('data-default-category') || '').toLowerCase();
  if (!category) return;

  const nav = dialog.querySelector('.tab-nav');
  if (!nav || nav.hasAttribute(APPLIED_FLAG)) return;

  const tabName = resolveTabName(category);
  if (!tabName) return;

  const tab = nav.querySelector(`.tab-item[data-tab="${tabName}"]`);
  if (!tab) return;

  nav.setAttribute(APPLIED_FLAG, '');
  tab.click();
  activateBraTable(dialog, category);
}

// 事件委托挂在 document 上：弹窗内容会被 morph 反复重建，绑在具体元素上的监听会丢。
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element
    ? event.target.closest('.fanka-size-guide-link__trigger')
    : null;
  if (!trigger) return;

  const dialog = trigger.closest('dialog-component')?.querySelector(DIALOG_SELECTOR);
  if (!dialog) return;

  // 让 Horizon 的 showDialog 先把 dialog 打开，再切 tab
  requestAnimationFrame(() => applyDefaultTab(dialog));
});
